import { UpperCasePipe } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, QueryList, signal, ViewChildren } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { APP_CONFIG, dateFormat, DeviceEnum, PAGE, SearchFields, SearchItems } from '../../core';
import { ListService, UserService } from '../../services';
import { AnatomicalSideLabelPipe, BaseComponent, BoneTypeLabelPipe, ConvertDatePipe, DeviceLabelPipe, PlanStatusClassEnum, PlanStatusLabelPipe, PlanStatusPipe, PlanStepLabelPipe, PlanTypeLabelPipe } from '../../shared';
import { PaginationComponent } from '../pagination';
import { PlanActionComponent } from '../plan-action';
import { SortableHeaderComponent, SortEvent } from '../sortable-header';


/**
* Plan table component.
*/
@Component({
	selector: 'plan-table',
	templateUrl: './plan-table.component.html',
	imports: [
		UpperCasePipe,
		PlanStatusPipe,
		ConvertDatePipe,
		DeviceLabelPipe,
		PlanTypeLabelPipe,
		BoneTypeLabelPipe,
		PlanStepLabelPipe,
		PlanStatusLabelPipe,
		PaginationComponent,
		PlanActionComponent,
		AnatomicalSideLabelPipe,
		SortableHeaderComponent
	],
	changeDetection: ChangeDetectionStrategy.Eager
})
export class PlanTableComponent extends BaseComponent implements OnInit, AfterViewInit, OnDestroy {

	private readonly router = inject(Router);
	private readonly config = inject(APP_CONFIG);
	private readonly listSrv = inject(ListService);
	private readonly userSrv = inject(UserService);

	@ViewChildren(SortableHeaderComponent) headers = new QueryList<SortableHeaderComponent>();
	activeColumn = signal<string | null>(null);

	collectionSize = signal<number>(0);
	planList = signal<SearchItems[]>([]);
	fields = SearchFields;
	readonly deviceEnum = DeviceEnum;
	readonly dateFormat = dateFormat();
	readonly statusClassEnum = PlanStatusClassEnum;

	isTlhexUser = toSignal(this.userSrv.isTlhex());
	isFiltersCollapsed = toSignal(this.listSrv.isCollapsed);

	private _searchResultSub$?: Subscription;


	ngOnInit(): void {
		this.listSrv.resetSearchResult();
		this.listSrv.resetPlanSearch();
		this._searchResultSub$ = this.listSrv.getSearchResult().subscribe(list => {
			this.collectionSize.set(list ? list.count : 0);
			this.planList.set(list ? list.items : []);
		});
	}

	ngAfterViewInit(): void {
		setTimeout(() => this.updateSortHeaders());
	}

	private updateSortHeaders(): void {
		const sortOrder = this.listSrv.getSortOrder();
		if (sortOrder) {
			this.headers.find(header => header.name() == sortOrder.name)?.direction.set(sortOrder.desc ? 'desc' : 'asc');
			this.activeColumn.set(sortOrder.name);
		}
	}

	/**
	* Update column sort order and sort list.
	*/
	onSort({ column, direction }: SortEvent): void {
		// resetting other headers
		for (const header of this.headers.filter(h => h.name() !== column)) {
			header.reset();
		}
		// sorting
		if (direction === '') {
			direction = column === this.listSrv.PLAN_DEFAULT_SORT.name || !this.listSrv.PLAN_DEFAULT_SORT.desc ? 'asc' : 'desc';
			column = this.listSrv.PLAN_DEFAULT_SORT.name;
		}
		this.listSrv.sort(column, direction);
		this.updateSortHeaders();
	}

	/**
	 * Check if plan is editable.
	 */
	isEditable(plan: SearchItems): boolean {
		return this.isPlanVersionValid(plan) && !plan.caseIsPublished && !plan.caseIsRevoked;
	}

	private isPlanVersionValid(plan: SearchItems): boolean {
		return !!plan?.caseVersion && plan.caseVersion >= this.config.appVersion;
	}

	/**
	* Navigate to plan edit page.
	*/
	editPlan(planGuid: string) {
		this.router.navigate([`/${PAGE.PLAN_EDIT}`], { queryParams: { planGuid: planGuid } });
	}


	ngOnDestroy(): void {
		this._searchResultSub$?.unsubscribe();
		this.listSrv.resetSearchResult();
	}

}
